// LotWise Auto shared site data.
// Copy discipline: concrete, operational, dealership-first English.
// No em-dashes. Use "dealership / dealer / inventory / vehicle merchandising".

export const BRAND = {
  name: "LotWise Auto",
  short: "LotWise",
  email: "hello@lotwiseauto.ca",
  phone: "(343) 202-7479",
  phoneHref: "tel:+13432027479",
  region: "Southeastern Ontario",
  tagline: "Dealership inventory, consistently market ready.",
};

export const LOGO = {
  wOnBlack:
    "https://customer-assets.emergentagent.com/job_inventory-standard/artifacts/jza1e9h9_Pp.jpg",
  wOnWhite:
    "https://customer-assets.emergentagent.com/job_inventory-standard/artifacts/jza1e9h9_Pp.jpg",
  fullOnWhite:
    "https://customer-assets.emergentagent.com/job_29794d15-1a6a-490b-aa22-b77286667b3c/artifacts/bunl0jmf_E5C77571-6C61-49F4-B81E-300ABE1D7C89.jpeg",
};

// Editorial imagery pulled from the LotWise Auto brand asset set.
// Treated deliberately in-page: spotlighted, clipped, or masked.
export const EDITORIAL_IMAGES = {
  service01:
    "https://customer-assets-lxgj4vgw.emergentagent.net/job_inventory-standard/artifacts/bfdlbim0_ChatGPT%20Image%20Aug%2011%2C%202026%2C%2003_16_20%20PM%20%282%29.png",
  service02:
    "https://customer-assets-lxgj4vgw.emergentagent.net/job_inventory-standard/artifacts/ar9ohqgy_ChatGPT%20Image%20Aug%2011%2C%202026%2C%2003_16_31%20PM%20%284%29.png",
  service03:
    "https://customer-assets-lxgj4vgw.emergentagent.net/job_inventory-standard/artifacts/oaclzlna_ChatGPT%20Image%20Aug%2011%2C%202026%2C%2003_16_31%20PM%20%283%29.png",
  service04:
    "https://customer-assets-lxgj4vgw.emergentagent.net/job_inventory-standard/artifacts/470bdjqd_ChatGPT%20Image%20Aug%2011%2C%202026%2C%2003_16_31%20PM%20%282%29.png",
  service05:
    "https://customer-assets-lxgj4vgw.emergentagent.net/job_inventory-standard/artifacts/8u6f144g_ChatGPT%20Image%20Aug%2011%2C%202026%2C%2003_16_30%20PM%20%281%29.png",
};

// Two service paths. Renamed to dealership operational language.
export const OFFER_PATHS = [
  {
    id: "scheduled_lot",
    name: "Scheduled Lot Service",
    kicker: "Recurring onsite operation",
    tagline:
      "A recurring dealership visit that keeps your inventory market ready. LotWise Auto arrives on your cadence, works the fresh units, and holds the listing standard for the platforms that drive your leads.",
    bestFor:
      "Dealerships that already have some internal photo or lot process and want a stronger, more consistent inventory presentation without a full handoff.",
    includes: [
      "Two LotWise Operators onsite per scheduled visit",
      "Fresh units photographed, detailed, and listed",
      "Cross-platform publishing across AutoTrader, CarGurus, FB Marketplace, Kijiji Autos",
      "Between-visit sold-unit removal and price updates",
      "One dealership standard, held every visit",
    ],
    cta: "See if it fits your dealership",
  },
  {
    id: "managed_inventory",
    name: "Managed Inventory Service",
    kicker: "Fully managed dealership merchandising",
    tagline:
      "The complete LotWise Auto operating standard. We own the vehicle merchandising process end-to-end so your dealership team carries zero listing drag.",
    bestFor:
      "Higher-volume dealerships, dealer groups, and stores that want the strongest inventory standard with the least internal burden on the sales and management team.",
    includes: [
      "Full ownership of photography, detailing, listing build, and updates",
      "Priority scheduling and higher visit cadence available",
      "Multi-platform inventory management across every priority channel",
      "Sold-unit removal, price changes, and re-photograph on demand",
      "Dedicated LotWise merchandising lead for your dealership",
    ],
    premium: true,
    cta: "Talk to LotWise about your dealership",
  },
];

// Service-model options used to qualify leads.
export const SERVICE_MODELS = [
  { value: "scheduled_lot", label: "Scheduled Lot Service" },
  { value: "managed_inventory", label: "Managed Inventory Service" },
  { value: "not_sure", label: "Not sure yet" },
];

// Side-by-side comparison rows (for ServiceComparison tabbed table).
export const SERVICE_COMPARISON_ROWS = [
  {
    label: "Who owns the standard",
    scheduled: "LotWise, on visit cadence",
    managed: "LotWise, end-to-end",
  },
  {
    label: "Onsite operators per visit",
    scheduled: "Two",
    managed: "Two + merchandising lead",
  },
  {
    label: "Photography",
    scheduled: "Full onsite photography of fresh units",
    managed: "Full onsite photography, re-shoots on demand",
  },
  {
    label: "Vehicle details",
    scheduled: "Options, condition, mileage, trim",
    managed: "Options, condition, mileage, trim, descriptions written",
  },
  {
    label: "Cross-platform publishing",
    scheduled: "Your priority channels",
    managed: "Every relevant channel, managed by LotWise",
  },
  {
    label: "Between-visit maintenance",
    scheduled: "Sold-unit removal, price changes",
    managed: "Sold-unit removal, price changes, listing updates, re-shoots",
  },
  {
    label: "Dealership internal effort",
    scheduled: "Light coordination",
    managed: "Near-zero",
  },
  {
    label: "Best for",
    scheduled: "Independent and mid-size dealerships",
    managed: "High-volume dealerships and dealer groups",
  },
];

// A "visit" is one of the most-asked FAQ items. Elevated to its own block.
export const VISIT_DEFINITION = {
  headline: "What a LotWise visit actually includes.",
  intro:
    "A visit is one full onsite operation at your dealership. Not a photo drop-off. Not a walk-through. A working session run by two LotWise Operators.",
  steps: [
    {
      k: "01",
      title: "Arrive and align",
      body: "Two LotWise Operators arrive at your dealership, confirm the fresh-unit queue with your sales manager, and lock the scope for the visit.",
    },
    {
      k: "02",
      title: "Photograph the inventory",
      body: "Full exterior and interior photography, shot to a consistent LotWise angle library so every vehicle reads the same across every listing.",
    },
    {
      k: "03",
      title: "Capture vehicle details",
      body: "Options, condition notes, mileage, trim, features. The full detail set that makes a listing actually complete, not just visible.",
    },
    {
      k: "04",
      title: "Build and publish the listings",
      body: "Each unit is composed to the LotWise standard and published across your priority platforms: AutoTrader, CarGurus, Facebook Marketplace, Kijiji Autos, and your dealership website.",
    },
    {
      k: "05",
      title: "Maintain between visits",
      body: "Sold units come down. Price changes go live. Fresh arrivals move into the next queue. Your inventory always reflects the actual lot.",
    },
  ],
  footnote:
    "Every visit runs on the same operational rhythm. Same standard. Same execution. Same output your buyers see online.",
};

// Numbered manifesto chapters. Replaces the older scattered
// OperationalBreakdown / WhyItMatters / NotSalesTeam blocks.
export const MANIFESTO = [
  {
    n: "01",
    kicker: "The dealership problem",
    title: "Inventory presentation is not a task. It is an operation.",
    body:
      "Most dealerships do not have a photo problem or a listing problem. They have an ownership problem. When responsibility for the online lot floats between sales, management, and whoever has time on a Tuesday, the standard slips wherever the store gets busy. Which is most days.",
  },
  {
    n: "02",
    kicker: "What buyers see",
    title: "The online lot is the first sales moment.",
    body:
      "By the time a buyer walks into your dealership, the first impression is already made. The listing set the tone. The photos set the expectations. Every stale unit, missing photo, or uneven listing quietly costs trust, days before your sales team ever gets to have the conversation.",
  },
  {
    n: "03",
    kicker: "Why not internal",
    title: "Salespeople should be selling. Managers should be closing.",
    body:
      "Asking your sales team to hold the listing standard between calls turns your highest-leverage staff into part-time photographers. The math never works. Standards drift, appointments slip, and managers end up chasing listings they should not have to look at.",
  },
  {
    n: "04",
    kicker: "The LotWise answer",
    title: "Handled as a system. Priced as an operation.",
    body:
      "LotWise Auto is a dedicated dealership inventory operation. Recurring onsite visits, one photography standard, one detail standard, one publishing standard, one owner of the outcome. Predictable cost, predictable output, no internal drag.",
  },
];

// Dealer use-case scenarios. Each maps a specific dealership pain
// pattern to the LotWise service that fits.
export const USE_CASES = [
  {
    slug: "independent-used",
    who: "Independent used-vehicle dealerships",
    pain: "Photos handled by whoever has a phone. Listings uneven across platforms. The lot looks bigger onsite than it does online.",
    fit: "Scheduled Lot Service",
    recommend:
      "A recurring visit brings the photo standard, the detail standard, and the cross-platform standard onto your inventory without rebuilding your internal process.",
  },
  {
    slug: "high-volume",
    who: "High-volume dealerships",
    pain: "Fresh units move in and out weekly. Internal teams cannot keep the listing pipeline current between other priorities.",
    fit: "Managed Inventory Service",
    recommend:
      "A fully managed operation with higher visit cadence, dedicated merchandising oversight, and same-week publishing on every incoming unit.",
  },
  {
    slug: "dealer-groups",
    who: "Multi-rooftop dealer groups",
    pain: "Standards vary between rooftops. Group leadership cannot enforce one consistent inventory presentation across every store.",
    fit: "Managed Inventory Service",
    recommend:
      "One LotWise standard, applied consistently across every rooftop, with a dedicated merchandising lead coordinating cadence group-wide.",
  },
  {
    slug: "inventory-backlog",
    who: "Dealerships with an inventory backlog",
    pain: "Units are sitting on the lot without proper listings. The store is paying to hold vehicles that are effectively invisible online.",
    fit: "Scheduled Lot Service",
    recommend:
      "A concentrated visit sequence to clear the backlog, then a recurring cadence to keep it from re-forming. Backlog becomes a one-time event.",
  },
  {
    slug: "growing-dealer",
    who: "Growing dealerships adding volume",
    pain: "The store is scaling faster than internal listing capacity. Every added unit adds internal drag.",
    fit: "Scheduled Lot Service (with room to graduate)",
    recommend:
      "Start on the recurring cadence to hold the standard as you scale. Graduate to the Managed Inventory Service when volume outgrows internal capacity.",
  },
];

// Simplified 5-step workflow for the visual horizontal timeline.
// The older 7-step data is kept for the /about deep dive.
export const WORKFLOW = [
  {
    k: "01",
    title: "Vehicle enters the lot",
    body: "Your team receives fresh inventory. The unit is on the ground, ready to be merchandised.",
  },
  {
    k: "02",
    title: "LotWise scheduled visit",
    body: "Two operators arrive on your set cadence. The fresh queue is confirmed with your sales manager.",
  },
  {
    k: "03",
    title: "Photographed and detailed",
    body: "Full onsite photography plus the options, condition, and detail capture that makes a listing complete.",
  },
  {
    k: "04",
    title: "Listing built and published",
    body: "Each unit is composed to the LotWise standard and published across your priority platforms in one pass.",
  },
  {
    k: "05",
    title: "Maintained between visits",
    body: "Sold units come down. Prices update. Fresh arrivals enter the next queue. The lot online matches the lot onsite.",
  },
];

// Original 7-step retained for the /about detailed walk.
export const HOW_IT_WORKS = [
  { step: "01", title: "Visit the dealership", body: "A team of two LotWise Operators arrives onsite on your scheduled cadence, ready to work, not to be managed." },
  { step: "02", title: "Review incoming units", body: "We walk the fresh inventory with your team, confirm what needs to be processed, and lock the queue for the visit." },
  { step: "03", title: "Capture photos and vehicle details", body: "Full photography onsite plus the options, condition notes, and details that make a listing actually complete." },
  { step: "04", title: "Build the listing properly", body: "Every unit is composed to a consistent standard: clean images, structured details, formatting that reads as handled." },
  { step: "05", title: "Publish where it matters", body: "Listings go live across Facebook Marketplace, AutoTrader, CarGurus, Kijiji Autos, and other relevant channels." },
  { step: "06", title: "Keep everything current", body: "Sold units come down. Price changes go up. New arrivals move into the queue. Inventory always reflects the lot." },
  { step: "07", title: "Maintain the standard", body: "Same rhythm, same standard, every visit. The lot standard shows up online because a system is holding it." },
];

// Trust and proof placeholders. Testimonials swap in as real quotes
// arrive; platform badges are permanent brand references.
export const PLATFORM_BADGES = [
  { name: "AutoTrader", note: "Cross-platform publishing" },
  { name: "CarGurus", note: "Cross-platform publishing" },
  { name: "Facebook Marketplace", note: "Cross-platform publishing" },
  { name: "Kijiji Autos", note: "Cross-platform publishing" },
  { name: "Dealership website", note: "Direct sync" },
];

export const TESTIMONIALS = [
  {
    quote:
      "We stopped asking our sales team to handle photos. The listings finally look like the lot actually looks.",
    role: "Used Vehicle Manager",
    where: "Independent dealership, Ottawa Valley",
  },
  {
    quote:
      "The recurring cadence is what changed things. It is not a photo shoot. It is an operation showing up every week.",
    role: "Dealership Principal",
    where: "SE Ontario",
  },
  {
    quote:
      "Every rooftop now looks the same online. That was the point.",
    role: "Group Operations Lead",
    where: "Multi-rooftop dealer group",
  },
];

// Short critical FAQ, elevated near the top of the page.
export const FAQ_QUICK = [
  {
    q: "What is a LotWise visit?",
    a: "One full onsite operation at your dealership. Two operators, photography, vehicle details, listings built, and everything published across your priority platforms in the same pass.",
  },
  {
    q: "Do you write the vehicle descriptions?",
    a: "Yes. On the Managed Inventory Service, LotWise writes the full description set. On the Scheduled Lot Service, we handle the structured detail capture so your existing template stays consistent.",
  },
  {
    q: "How often do you visit?",
    a: "That depends on your inventory turnover. Some dealerships run a monthly cadence, others multiple times a week. We recommend a rhythm based on your fresh-unit volume.",
  },
  {
    q: "Do you replace our current process?",
    a: "Only if you want us to. Scheduled Lot Service integrates with your existing team. Managed Inventory Service takes the operation off your team entirely.",
  },
  {
    q: "How is pricing structured?",
    a: "Priced by dealership operation, not per photo. We scope the right rhythm on a short call so the cost matches your actual inventory volume and service model.",
  },
];

// Full FAQ (kept for /faq page and structured data).
export const FAQ = [
  {
    q: "What is a LotWise visit?",
    a: "One full onsite operation at your dealership. Two LotWise Operators arrive on your scheduled cadence, confirm the queue with your sales manager, photograph the fresh inventory, capture vehicle details, build each listing to the LotWise standard, and publish across your priority platforms in the same pass.",
  },
  {
    q: "What exactly does LotWise Auto handle?",
    a: "LotWise Auto manages dealership vehicle listings end to end. Onsite photography, capturing the options and condition details that complete a listing, formatting each listing to a consistent standard, publishing across the platforms that drive your leads, and keeping everything current between visits.",
  },
  {
    q: "Do you take the photos?",
    a: "Yes. Every visit includes two LotWise Operators onsite handling the full photography requirement for the units in the queue. Photos are captured, sorted, and processed as part of the same execution flow that publishes the listing.",
  },
  {
    q: "Do you write vehicle descriptions?",
    a: "On the Managed Inventory Service, yes. LotWise composes the full description set for each listing. On the Scheduled Lot Service, we handle the structured detail capture so your existing description template stays consistent, complete, and accurate.",
  },
  {
    q: "How often do you visit a dealership?",
    a: "It depends on your fresh-unit volume. Some dealerships run a monthly rhythm. Others operate on twice-weekly cadence. We recommend the right cadence based on how quickly your inventory turns.",
  },
  {
    q: "Does LotWise Auto fully replace our current process?",
    a: "Only if you want it to. The Scheduled Lot Service integrates into your existing dealership workflow. The Managed Inventory Service takes the entire inventory merchandising operation off your team.",
  },
  {
    q: "Can LotWise work with our existing team?",
    a: "Yes. The Scheduled Lot Service is designed for dealerships that already have some internal photography or lot process in place. LotWise strengthens the standard on top of your workflow, without forcing you to rebuild it.",
  },
  {
    q: "What is included in the Scheduled Lot Service?",
    a: "A recurring dealership visit by two LotWise Operators, full onsite photography, structured vehicle detail capture, listings composed to the LotWise standard, cross-platform publishing to your priority channels, and between-visit maintenance for sold-unit removal and price updates.",
  },
  {
    q: "What is included in the Managed Inventory Service?",
    a: "The full LotWise operation. Photography, detailing, description writing, listing build, cross-platform publishing across every relevant channel, sold-unit removal, price changes, re-shoots on demand, and a dedicated LotWise merchandising lead for your dealership.",
  },
  {
    q: "How do we know which service model fits our dealership?",
    a: "It comes down to how much of the operation you want to keep in-house. If you want to keep your team engaged and add the LotWise standard, Scheduled Lot Service usually fits. If you want the operation off your team entirely, Managed Inventory Service is the better fit. If you are not sure, use the Interactive Dealer Qualifier and we will recommend the right model.",
  },
  {
    q: "Can we start with the recurring cadence and expand later?",
    a: "Yes. Many dealerships begin with the Scheduled Lot Service and graduate to the Managed Inventory Service as their inventory volume outgrows internal capacity. The standard stays consistent. The scope adjusts.",
  },
  {
    q: "Why not have our own staff handle this?",
    a: "Most dealerships can hold a strong photo and listing standard for a week or two. Holding it every week, for every unit, across every platform, without slipping, requires a dedicated operation. When the responsibility gets split across an already busy sales floor, standards drift.",
  },
  {
    q: "Is LotWise Auto more economical than hiring internally?",
    a: "Once labour, employer payroll costs, training, tools, software, oversight, coverage during absences, and inconsistency are added up, internal handling often costs more than it first appears, and the standard is still uneven. LotWise Auto delivers a controlled standard at a predictable, structured cost, without the internal burden. We walk you through the specifics for your dealership on a call.",
  },
  {
    q: "How is pricing structured?",
    a: "Pricing depends on the service model and the visit cadence your dealership needs. Rather than a one-size-fits-all number, we scope it to your actual inventory operation on a short call so you only pay for the level of service that fits.",
  },
  {
    q: "What happens after we request a visit?",
    a: "A LotWise Auto lead handler will contact you to confirm your inventory rhythm, walk you through the standard, and schedule a first onsite. Southeastern Ontario dealerships are routed into active scheduling. Dealerships outside the region are added to the waitlist.",
  },
  {
    q: "Which areas do you serve?",
    a: "LotWise Auto currently serves southeastern Ontario. Including Ottawa, Pembroke, Petawawa, Perth, Arnprior, Renfrew, Carleton Place, Rockland, Cornwall, Kingston, and the surrounding Ottawa Valley and eastern Ontario corridor.",
  },
  {
    q: "Can we apply if we are outside southeastern Ontario?",
    a: "Yes. We accept waitlist applications from dealerships across Canada. As LotWise Auto expands service coverage, waitlisted dealerships are contacted in order.",
  },
];

export const SERVICE_AREA = {
  primary: [
    "Ottawa",
    "Kingston",
    "Cornwall",
    "Pembroke",
    "Petawawa",
    "Perth",
    "Arnprior",
    "Renfrew",
    "Carleton Place",
    "Rockland",
  ],
  regions: [
    "Ottawa Valley",
    "Lanark County",
    "Renfrew County",
    "Prescott and Russell",
    "Stormont, Dundas and Glengarry",
    "Leeds and Grenville",
    "Frontenac County",
  ],
};

export const PLATFORMS = [
  "Facebook Marketplace",
  "AutoTrader",
  "CarGurus",
  "Kijiji Autos",
  "Dealership website",
  "Instagram",
];

// ARTICLES corpus lives in ./articles.js — import directly from there.

export const TRUST_METRICS = [
  { label: "Onsite operators per visit", value: "2" },
  { label: "Priority platforms", value: "5+" },
  { label: "Priority region", value: "SE Ontario" },
  { label: "Dealership standard", value: "One" },
];

export const BEFORE_AFTER = [
  { slug: "silver-suv", year: "2021", trim: "Mid-size SUV", label: "Silver SUV" },
  { slug: "red-pickup", year: "2022", trim: "Full-size Pickup", label: "Red Pickup" },
  { slug: "white-sedan", year: "2020", trim: "Sedan", label: "White Sedan" },
  { slug: "black-suv", year: "2023", trim: "3-Row SUV", label: "Black SUV" },
];

export const NAV = [
  { to: "/", label: "Home" },
  { to: "/sample-listings", label: "Sample Listings" },
  { to: "/faq", label: "FAQ" },
  { to: "/insights", label: "Insights" },
  { to: "/about", label: "About" },
];
